/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import { makeStyles } from '@mui/styles';
import FormControl from 'apollo-react/components/FormControl';
import ApolloRichTextEditor from 'apollo-react/components/RichTextEditor';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';

import ReadOnlyRichText from './ReadOnlyRichText';
import RuntimeTranslation from '../../../../components/RuntimeTranslation';
import { useTranslation } from '../../../../custom-hooks/useTranslation';
import { checkTrue, getUITA } from '../../../../utils/commonUtils';
import { isNilOrEmpty } from '../../../../utils/ramdaUtils';
import { SS_CONTROL_TYPE } from '../../../constants/form-manager.constants';
import {
  contentHasText,
  convertHtmlToRawContentState,
  convertRawContentStateToHtml,
} from '../../../utils/controls/ssRichTextEditor.utils';
import ReadOnlyInputControl from '../../ReadOnlyInputControl';

const memoizedHtmlToRawContentState = _.memoize(convertHtmlToRawContentState, _.identity);

const useStyles = makeStyles({
  container: {
    minWidth: 120,
    maxWidth: 'none',
    width: '100%',
    '&:focus': {
      outline: 'none',
    },
    display: 'flex',
  },
  readOnlyContainer: {
    maxWidth: '568px',
    alignItems: 'center',
  },
  readOnlyTranslationContainer: {
    maxWidth: '608px',
  },
});

const SSRichTextEditor = ({
  label,
  isRequired,
  instanceId,
  isDetail,
  isEditor,
  isViewOnly,
  actions,
  entityName,
  fieldName,
  value,
  inTable,
  isHiddenLabel,
  inFieldGroup,
  translationServiceEnabledFlag,
  disabled,
  error,
}) => {
  const classes = useStyles();
  const translateString = useTranslation();
  const [contentState, setContentState] = useState(null);
  const [textValue, setTextValue] = useState(value ?? '');
  const [key, setKey] = useState(1);
  const [isInternalChange, setInternalChange] = useState(false);

  const handleBlur = () => {
    const html = contentHasText(contentState) ? convertRawContentStateToHtml(contentState) : '';
    actions.updateControlValue(html);
    setInternalChange(true);
    setTextValue(html);
  };

  const handleRichTextEditorChange = (rawContentState) => {
    setContentState(rawContentState);
  };

  const defaultRawContentState = !isNilOrEmpty(value)
    ? memoizedHtmlToRawContentState(value)
    : '---';

  useEffect(() => {
    if (!isInternalChange) {
      setKey(key + 1);
    }
    setInternalChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRawContentState]);

  const readOnlyRichText = (
    <ReadOnlyRichText id={instanceId} defaultValue={defaultRawContentState} key={key} />
  );

  if ((isViewOnly || isDetail) && !isEditor) {
    return (
      <ReadOnlyInputControl
        uita={getUITA('SSTextArea', entityName, fieldName)}
        label={label}
        value={!isNilOrEmpty(value) ? readOnlyRichText : '---'}
        inTable={inTable}
        isHiddenLabel={isHiddenLabel}
        inFieldGroup={inFieldGroup}
        entityName={entityName}
        fieldName={fieldName}
        controlType={SS_CONTROL_TYPE.TEXT_AREA}
        isEditor={isEditor}
        containerStyles={
          isDetail
            ? null
            : checkTrue(translationServiceEnabledFlag)
            ? classNames(classes.readOnlyContainer, classes.readOnlyTranslationContainer)
            : classes.readOnlyContainer
        }
      />
    );
  }

  return (
    <span ui-ta={getUITA('FormCtrl', 'SSRichTextArea', entityName, fieldName)}>
      <div
        id={instanceId}
        className={
          isDetail
            ? classes.containerDetail
            : classNames(classes.container, isViewOnly ? classes.containerReadOnly : '')
        }
        tabIndex={0}
      >
        <FormControl margin={isDetail ? 'none' : 'normal'} fullWidth>
          <ApolloRichTextEditor
            label={!isDetail && label}
            required={isRequired}
            defaultValue={defaultRawContentState}
            readOnly={isViewOnly}
            variant={isDetail || isViewOnly ? 'view' : 'inline'}
            placeholder={isDetail || isViewOnly ? '' : `${translateString('Enter text here')}...`}
            onChange={handleRichTextEditorChange}
            onBlur={handleBlur}
            error={error}
            disabled={isEditor || disabled}
            key={key}
          />
        </FormControl>
        <RuntimeTranslation value={textValue} isReadOnly={isDetail} />
      </div>
    </span>
  );
};

export default SSRichTextEditor;
